import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setOfferCategoryInfo } from '../slices/offerCategorySlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const offerCategoriesApi = createApi({
  reducerPath: 'offerCategoriesApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['OfferCategoryInfo', 'OfferCategories'],
  endpoints: (builder) => ({
    getOfferCategories: builder.query({
      query: (requestParams: CommonSearchParams) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'getOfferCategories',
          requestParams
        ),
      providesTags: ['OfferCategories']
    }),
    getOfferCategoryInfo: builder.query({
      query: (requestParams: GetOfferCategoryInfoParams) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'getOfferCategoryInfoByID',
          requestParams
        ),
      providesTags: [{ type: 'OfferCategoryInfo' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.result) {
            dispatch(setOfferCategoryInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    validateOfferCategoryFormFields: builder.mutation({
      query: (requestParams: OfferCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'validateOfferCategoryFormFields',
          requestParams
        )
    }),
    createNewOfferCategory: builder.mutation({
      query: (requestParams: OfferCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'createNewOfferCategory',
          requestParams
        ),
      invalidatesTags: ['OfferCategories']
    }),
    editOfferCategory: builder.mutation({
      query: (requestParams: OfferCategoryFormFields) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'editOfferCategory',
          requestParams
        ),
      invalidatesTags: [{ type: 'OfferCategoryInfo' }]
    }),
    updateCategoryUiPositions: builder.mutation({
      query: (requestParams: CategoryUpdateUiParams) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'updateUiPositions',
          requestParams
        ),
      invalidatesTags: ['OfferCategories']
    }),
    softDeleteOfferCategory: builder.mutation({
      query: (requestParams: { OfferCategoryID: number }) =>
        generateRPCQuery(
          apiConstants.OFFER_CATEGORIES_RPC_PATH,
          'softDeleteOfferCategory',
          requestParams
        ),
      invalidatesTags: [{ type: 'OfferCategories' }]
    })
  })
})

export const {
  useCreateNewOfferCategoryMutation,
  useEditOfferCategoryMutation,
  useUpdateCategoryUiPositionsMutation,
  useValidateOfferCategoryFormFieldsMutation,
  useGetOfferCategoriesQuery,
  useGetOfferCategoryInfoQuery,
  useSoftDeleteOfferCategoryMutation
} = offerCategoriesApi
